import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Zap, Clock, CheckCircle2, LinkIcon, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import UrlAnalyzer from "@/components/onboarding/UrlAnalyzer";
import TestOptions from "@/components/onboarding/TestOptions";
import RegistrationPrompt from "@/components/onboarding/RegistrationPrompt";
import { ConversionTracking, trackConversion } from "@/components/tracking/ConversionTracking";
import { CookieConsent } from "@/components/tracking/CookieConsent";
import SocialShareButtons from "@/components/SocialShareButtons";
import { SharedHeader } from "@/components/SharedHeader";

type FlowStep = 'url' | 'test' | 'register';

const UrlToAgent = () => {
  const {
    t
  } = useTranslation();
  const [flowStep, setFlowStep] = useState<FlowStep>('url');
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  const [demoQuery, setDemoQuery] = useState<string>('');
  // Track flow step changes for conversions
  useEffect(() => {
    if (flowStep === 'test') {
      trackConversion('AgentCreated', {
        event_category: 'Agent Creation',
        event_label: 'URL Method',
        value: 1
      });
    } else if (flowStep === 'register') {
      trackConversion('ReachedSignup', {
        event_category: 'Funnel',
        event_label: 'URL Method',
        value: 2
      });
    }
  }, [flowStep]);
  return <>
      <CookieConsent />
      <ConversionTracking pageName="URL to Agent Landing Page" />
      <Helmet>
        <title>AI Answering Service from Website | AI Call Center Software | Call24x7</title>
        <meta name="description" content="Build 24/7 AI answering service from your website automatically. AI call center software creates AI receptionist trained on your business in 60 seconds." />
        <meta name="keywords" content="24x7 AI answering service, AI call center software, AI customer support platform, AI receptionist service, AI customer service automation, how to automate customer calls with AI, AI tools for small business call handling, website to AI agent" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/url-to-agent" />
        <meta property="og:title" content="AI Answering Service from Website | AI Call Center Software" />
        <meta property="og:description" content="Build 24/7 AI answering service from your website. AI call center software creates AI receptionist trained on your business." />
        <meta property="og:image" content="https://call24x7.ai/og-image-url.png" />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://call24x7.ai/url-to-agent" />
        <meta name="twitter:title" content="AI Answering Service from Website | AI Call Center Software" />
        <meta name="twitter:description" content="Build 24/7 AI answering service from your website. AI call center software for small business call handling." />
        <meta name="twitter:image" content="https://call24x7.ai/og-image-url.png" />
        
        <link rel="canonical" href="https://call24x7.ai/url-to-agent" />
        
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Call24x7.AI URL to AI Phone Agent Builder",
          "applicationCategory": "BusinessApplication",
          "description": "Build 24/7 AI answering service from your website automatically. AI call center software creates AI receptionist trained on your business content. Perfect AI tools for small business call handling.",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "143"
          }
        })}
        </script>
        
        {/* Structured Data - HowTo */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Build AI Answering Service from Website URL",
          "description": "Create a 24/7 AI answering service automatically from your website for AI customer service automation",
          "step": [{
            "@type": "HowToStep",
            "name": "Enter Website URL",
            "text": "Paste your website URL to start building your AI call center software"
          }, {
            "@type": "HowToStep",
            "name": "AI Analysis",
            "text": "Our AI customer support platform reads and understands your website content, products, and services"
          }, {
            "@type": "HowToStep",
            "name": "Automatic Creation",
            "text": "Your AI receptionist service is created automatically with knowledge from your website"
          }, {
            "@type": "HowToStep",
            "name": "Test and Deploy",
            "text": "Test your new 24/7 AI answering service immediately and deploy to handle real calls"
          }],
          "totalTime": "PT1M"
        })}
        </script>
        
        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "How do I build AI answering service from my website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Simply enter your website URL and our AI call center software will analyze your site to automatically create a 24/7 AI answering service trained on your business content. It takes less than 60 seconds."
            }
          }, {
            "@type": "Question",
            "name": "What information does the AI customer support platform extract?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The AI reads your website content to understand your products, services, business hours, contact information, and how you communicate with customers. This knowledge powers your AI receptionist service."
            }
          }, {
            "@type": "Question",
            "name": "Can I test the AI answering service before using it?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! After the AI customer service automation agent is created, you can immediately test it with a real phone call to ensure it handles conversations correctly before deploying it to your business."
            }
          }]
        })}
        </script>
        
        {/* Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "AI Answering Service from Website",
            "item": "https://call24x7.ai/url-to-agent"
          }]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <SharedHeader />

        {/* Hero Section */}
        <section className="pt-20 pb-4 px-4 md:px-4">
          <div className="container mx-auto max-w-6xl px-0 md:px-4 py-[35px] my-px">
            <div className="text-center mb-4 md:px-0 px-[17px]">
              
              <h1 className="font-bold mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent py-[25px] md:text-5xl text-3xl">{t('urlToAgent.heroTitle')}
              <br />
                
              </h1>
              
            </div>

            {/* How It Works */}
            {flowStep === 'url' && <div className="mb-4 px-4 md:px-0">
                
                

























              </div>}

            {/* Main Content - Flow Steps */}
            <div className="max-w-4xl mx-auto px-0 md:px-4">
              {flowStep === 'url' && <Card className="p-5 border-2 py-[24px] my-0 px-0">
                  <div className="mb-4">
                    
                    
                  </div>
                  <UrlAnalyzer key={demoQuery} initialQuery={demoQuery} onComplete={(config) => {
                setAgentConfig(config);
                setFlowStep('test');
              }} onBack={() => { setDemoQuery(''); window.location.href = '/'; }} />
                </Card>}

              {flowStep === 'test' && agentConfig && <div className="space-y-6">
                  <Card className="p-6 bg-primary/5 border-primary/20">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('urlToAgent.agentCreated')}</h3>
                        <p className="text-muted-foreground mb-4">
                          {t('urlToAgent.agentTrained', { name: agentConfig.name })}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">{agentConfig.type}</Badge>
                          {agentConfig.features?.map((feature: string, i: number) => <Badge key={i} variant="outline">{feature}</Badge>)}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <TestOptions agentConfig={agentConfig} sessionId={sessionId} agentId={agentId} onRegister={(sid, aid) => {
                setSessionId(sid);
                setAgentId(aid);
                setFlowStep('register');
              }} onBack={() => setFlowStep('url')} />
                </div>}

              {flowStep === 'register' && <RegistrationPrompt agentId={agentId} agentConfig={agentConfig} sessionId={sessionId} onComplete={() => {
              window.location.href = 'https://manage.call24x7.ai/signup';
            }} />}
            </div>

            {/* Benefits Grid */}
            {flowStep === 'url'}

            {/* Social Proof */}
            {flowStep === 'url' && <div className="mt-6 px-4 md:px-0">
                <div className="text-center mb-4">
                   <p className="text-xs text-muted-foreground mb-2">{t('urlToAgent.socialProofTitle')}</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {[
                      { labelKey: 'urlToAgent.ecommerce', query: 'Best Buy electronics e-commerce store' },
                      { labelKey: 'urlToAgent.medical', query: 'Family medical clinic healthcare practice' },
                      { labelKey: 'urlToAgent.saas', query: 'Salesforce SaaS CRM software company' },
                      { labelKey: 'urlToAgent.realEstate', query: 'RE/MAX real estate agency property listings' },
                      { labelKey: 'urlToAgent.restaurants', query: 'Italian restaurant fine dining reservations' },
                    ].map((item) => (
                      <Badge
                        key={item.labelKey}
                        variant="outline"
                        className="text-xs py-1 px-3 cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-colors"
                        onClick={() => setDemoQuery(item.query)}
                      >
                        <LinkIcon className="w-3 h-3 mr-1" />
                        {t(item.labelKey)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>}

            {flowStep === 'url'



          }
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t py-4 mt-8">
          <div className="container mx-auto px-4">
            <SocialShareButtons className="mb-4" />
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                {t('urlToAgent.copyright')}
              </p>
              <div className="flex gap-6">
                <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {t('urlToAgent.privacyPolicy')}
                </a>
                <a href="/#pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {t('urlToAgent.pricing')}
                </a>
                <a href="/#features" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {t('urlToAgent.features')}
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
};
export default UrlToAgent;